import { type FunctionComponent } from 'react';
import { useMatch } from 'react-router';
import { configureRoute } from '../../../utils/routes/configure-route';
import { Link } from '../../components/common-components/link/link.component';
import { PageContainer } from '../../components/common-components/page/page-container/page-container.component';
import { PanelComponent } from '../../components/common-components/panel-component/panel-component.component';
import { OrderLookupForm } from '../../components/order-lookup-form/order-lookup-form.component';
import { ACCOUNT_LOGIN_LINK, ORDER_SUMMARY_LINK, ORDER_TRACK_LINK, SET_UP_RETURN_LINK } from '../../constants/links';
import { PAGE_DESCRIPTION_META, PAGE_TITLE_META } from '../../constants/meta';
import { useTrackPageView } from '../../hooks/analytics/analytics.hooks';
import { useRouterLocation } from '../../hooks/routing/location.hook';

const HelpTextTooltip = (
	<PanelComponent headingContent={`How to find...`} containerClassName={`pa3`} headerClassName={'bg-theme-grey-lighter'}>
		<span id="w4 lh-copy ws-normal tc">
			<ul className="list pa0 f6-ns f7">
				<li>Find your Order Number in your email</li>
				<li className="normal">
					{' '}
					Search your email for <span className="theme-grey i">Build.com Order</span>
				</li>
				<li className="mt2">Find your PO Number on your Packing Slip</li>
				<li className="normal">If there is a dash in the string of numbers, omit the numbers before the dash</li>
			</ul>
		</span>
	</PanelComponent>
);

type GuestOrderLookupProps = {
	heading?: string;
};

export const GuestOrderLookup: FunctionComponent<GuestOrderLookupProps> = ({ heading = 'Track Order' }) => {
	const isOrderSummaryPage = useMatch(ORDER_SUMMARY_LINK);
	const isGuestSetupReturnPage = useMatch(SET_UP_RETURN_LINK);

	const urlParams = new URLSearchParams(useRouterLocation().search);
	const origin = urlParams.get('origin') || '';

	const isFromOrderStatusPage = Boolean(origin === 'order-status');

	const pageName = isOrderSummaryPage
		? 'ecrm:order_summary'
		: isGuestSetupReturnPage
		? 'ecrm:guest_setup_return'
		: 'ecrm:guest_order_lookup';
	useTrackPageView({ pageName });

	if (isOrderSummaryPage) {
		heading = 'Find Order Summary';
	}

	if (isGuestSetupReturnPage) {
		heading = 'Set up a Return';
	}

	const routeToReceiptPage = Boolean(isOrderSummaryPage || isGuestSetupReturnPage || isFromOrderStatusPage);

	return (
		<PageContainer canonicalURL={ORDER_TRACK_LINK} pageTitle={PAGE_TITLE_META} metaDescription={PAGE_DESCRIPTION_META}>
			<section data-testid="guest-order-lookup-section">
				<div className="flex flex-column items-center">
					<div className="w-100 w-50-ns relative">
						<PanelComponent headingContent={heading} className="f4 br2" headerClassName="pa2">
							<OrderLookupForm
								navigateToPage={routeToReceiptPage ? 'receipt' : 'shipments'}
								helpTextTooltip={HelpTextTooltip}
							/>
						</PanelComponent>
					</div>
					{routeToReceiptPage && (
						<div className="w-30-ns mt4 bg-theme-grey-lighter theme-grey-darker lh-copy">
							<div className="pa3 ba b--theme-grey-light br2 br--bottom">
								<div className="b f5 lh-title">Have an account?</div>
								Log in to see all your orders &amp; returns.
								<div>
									<Link url={ACCOUNT_LOGIN_LINK} className="link">
										Log in to My Account
									</Link>
								</div>
							</div>
						</div>
					)}
				</div>
			</section>
		</PageContainer>
	);
};

export const RoutedGuestOrderLookup = configureRoute(
	{ path: [ORDER_TRACK_LINK, ORDER_SUMMARY_LINK, SET_UP_RETURN_LINK] },
	GuestOrderLookup
);
